import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, CursorBox, Eye, Feather, Settings01, Stars02, Toggle03Right } from "@untitledui/icons";
import { useCallback, useState } from "react";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { PageLayout } from "@/components/page-layout";
import { RULE_TOOLTIP_CLASSNAME } from "@/components/rules/rule-source-tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { usePermissions } from "@/hooks/use-permissions";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import type { configureSafetyRoute } from "@/router";
import { ConfigureSafetyModal } from "./configure/components/ConfigureSafetyModal/ConfigureSafetyModal";
import { PolicyDetail } from "./configure/tabs/TabPolicies/PolicyDetail";
import { TabPolicies } from "./configure/tabs/TabPolicies/TabPolicies";
import { TabRisks } from "./configure/tabs/TabRisks/TabRisks";
import { SAFETY_ADMIN_REQUIRED, SAFETY_ADMINS_LINK } from "./permission-copy";

type TabId = "risks" | "policies";

const tabs = [
  { id: "risks", label: "Risks", icon: <AlertTriangle className="w-4 h-4" /> },
  { id: "policies", label: "Policies", icon: <Feather className="w-4 h-4" /> },
] satisfies { id: TabId; label: string; icon: React.ReactNode; badge?: string }[];

export const SafetyConfigure = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { canConfigureSafety } = usePermissions();
  const scopedUrl = useScopedUrl();

  // `tab` is the active tab; `policy` opens a single policy's detail view in place of
  // the tabs; `risk` selects one or more risks within the Risks tab.
  const {
    tab: activeTab,
    policy,
    risk: selectedRiskIds,
    focusRisk,
    focusAction,
  } = useSearch({ strict: false }) as {
    tab: TabId;
    policy?: string;
    risk?: string[];
    focusRisk?: string;
    focusAction?: "on" | "off";
  };
  const navigate = useNavigate() as ReturnType<(typeof configureSafetyRoute)["useNavigate"]>;
  const setActiveTab = useCallback(
    (key: TabId) =>
      navigate({
        search: (prev) => ({
          ...prev,
          tab: key,
          ...(key === "risks" ? { policy: undefined } : { risk: undefined }),
        }),
      }),
    [navigate],
  );
  const openPolicy = useCallback((id: string) => navigate({ search: (prev) => ({ ...prev, policy: id }) }), [navigate]);
  // Stable identity: passed down as `onBack`/`onSelectRisk`/`onRemoveRisks`, which effects
  // (e.g. auto-clearing a stale policy/risk id) depend on — a fresh function every render
  // would re-fire those effects on every unrelated re-render, not just on real changes.
  const closePolicy = useCallback(() => navigate({ search: (prev) => ({ ...prev, policy: undefined }) }), [navigate]);
  // Toggles a single risk id in/out of the selection, keeping every other one selected.
  const selectRisk = useCallback(
    (id: string) =>
      navigate({
        search: (prev) => {
          const current = prev.risk ?? [];
          const next = current.includes(id) ? current.filter((r) => r !== id) : [...current, id];
          return { ...prev, risk: next.length > 0 ? next : undefined };
        },
      }),
    [navigate],
  );
  // Drops ids that turned out to be stale (see TabRisks) instead of toggling one at a time.
  // A focused link opens the modal straight away; closing it drops the focus from the URL.
  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (!open)
        navigate({ search: (prev) => ({ ...prev, focusRisk: undefined, focusAction: undefined }), replace: true });
    },
    [navigate],
  );
  const removeInvalidRisks = useCallback(
    (invalidIds: string[]) =>
      navigate({
        search: (prev) => {
          const next = (prev.risk ?? []).filter((id) => !invalidIds.includes(id));
          return { ...prev, risk: next.length > 0 ? next : undefined };
        },
      }),
    [navigate],
  );

  if (policy) {
    return <PolicyDetail policyId={policy} onBack={closePolicy} />;
  }

  const tabContent: Record<TabId, React.ReactNode> = {
    risks: <TabRisks selectedRiskIds={selectedRiskIds} onSelectRisk={selectRisk} onRemoveRisks={removeInvalidRisks} />,
    policies: <TabPolicies onOpenPolicy={openPolicy} />,
  };

  return (
    <PageLayout
      title="Configure Safety"
      subtitle="Control what risks to monitor and how they should be handled"
      icon={CursorBox}
      backgroundColor="#FFFCF9"
      callToAction={
        canConfigureSafety ? (
          <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>
            Configure Risks
          </Button>
        ) : (
          // Users can view safety settings but not configure them (UNB-1912).
          <HoverCard openDelay={100} closeDelay={150}>
            <HoverCardTrigger asChild>
              <span>
                <Button color="primary" size="sm" iconLeading={<Settings01 className="w-4 h-4" />} isDisabled>
                  Configure Risks
                </Button>
              </span>
            </HoverCardTrigger>
            <HoverCardContent side="bottom" align="end" className={`${RULE_TOOLTIP_CLASSNAME} flex flex-col gap-1`}>
              <p>{SAFETY_ADMIN_REQUIRED.title}</p>
              <p className="font-medium text-tooltip-supporting-text">{SAFETY_ADMIN_REQUIRED.description}</p>
              <Link
                to={`${scopedUrl}/settings/team` as string}
                search={{ sort: "role" }}
                className="mt-1 underline underline-offset-2"
              >
                {SAFETY_ADMINS_LINK}
              </Link>
            </HoverCardContent>
          </HoverCard>
        )
      }
    >
      <PageHero
        pageKey={PageHeroKey.ConfigureSafety}
        title="Choose the risks we watch for in every video."
        text1={{ icon: Toggle03Right, message: "Turn risks categories on or off, from claims to music." }}
        text2={{ icon: Stars02, message: "Our AI checks each video against the risks you pick." }}
        text3={{ icon: Eye, message: "See what's monitored and act on it with your own policies." }}
        image="/heroes/configure-safety.png"
      />
      <div className="flex-shrink-0">
        <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as TabId)}>
          <TabList type="underline" items={tabs}>
            {(tab) => <Tab key={tab.id} id={tab.id} label={tab.label} icon={tab.icon} badge={tab.badge} />}
          </TabList>
        </Tabs>
      </div>

      {tabContent[activeTab]}

      <ConfigureSafetyModal
        open={(modalOpen || !!focusRisk) && canConfigureSafety}
        onOpenChange={handleModalOpenChange}
        focus={focusRisk ? { riskId: focusRisk, action: focusAction ?? "on" } : undefined}
      />
    </PageLayout>
  );
};
